// 极光罗盘自研埋点 SDK —— 核心逻辑（框架无关，Nuxt 与 SPA 各放一份）
// 职责：匿名访客ID / UTM 首次-末次触点 / 会话 / PV / 自定义事件 / identify 合并 / sendBeacon 上报
// 详见 plan D
// ⚠️ 所有公共方法 try-catch 兜底：埋点绝不能影响业务流程（路由跳转/登录等）

const ANON_KEY = 'jg_anonymous_id'
const FIRST_TOUCH_KEY = 'jg_first_touch'
const LAST_TOUCH_KEY = 'jg_last_touch'
const SESSION_KEY = 'jg_session_id'
const SESSION_LAST_KEY = 'jg_session_last_active'
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 分钟无活动即新建会话
const BATCH_SIZE = 20
const BATCH_INTERVAL = 10 * 1000 // 10s 刷一次

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getStorage(key, isSession) {
  try {
    return isSession ? sessionStorage.getItem(key) : localStorage.getItem(key)
  } catch (e) { return null }
}
function setStorage(key, val, isSession) {
  try {
    isSession ? sessionStorage.setItem(key, val) : localStorage.setItem(key, val)
  } catch (e) { /* 隐私模式无存储，降级 cookie */ }
}
function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return m ? m[2] : null
  } catch (e) { return null }
}
function setCookie(name, val, days) {
  try {
    const d = new Date(Date.now() + days * 864e5).toUTCString()
    document.cookie = name + '=' + val + ';expires=' + d + ';path=/;max-age=' + (days * 86400)
  } catch (e) { /* cookie 不可用时忽略 */ }
}

function parseUtm(query) {
  try {
    const params = new URLSearchParams(query || '')
    const utm = {}
    ;['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(k => {
      const v = params.get(k)
      if (v) utm[k] = v
    })
    return utm
  } catch (e) { return {} }
}

export default class Tracker {
  constructor(options = {}) {
    this.endpoint = options.endpoint || '/track/event'
    this.identifyEndpoint = options.identifyEndpoint || '/track/identify'
    this.userId = null
    this.tenantId = null
    this.queue = []
    this.timer = null
    this.anonymousId = null
    this.sessionId = null
    // 初始化失败不影响对象创建（后续方法 try-catch 兜底）
    try { this._init() } catch (e) { /* 静默：不影响业务 */ }
  }

  // ---- 初始化：匿名ID / 会话 / UTM 首末次触点 ----
  _init() {
    if (typeof window === 'undefined') return

    // 匿名访客 ID（localStorage，cookie 兜底 30 天）
    let anonId = getStorage(ANON_KEY) || getCookie(ANON_KEY)
    if (!anonId) {
      anonId = uuid()
      setStorage(ANON_KEY, anonId)
      setCookie(ANON_KEY, anonId, 30)
    }
    this.anonymousId = anonId

    // UTM 首次触点（不可变，仅首次访问写入）
    const utm = parseUtm(window.location.search)
    const referrer = document.referrer
    const firstTouch = getStorage(FIRST_TOUCH_KEY)
    if (!firstTouch) {
      setStorage(FIRST_TOUCH_KEY, JSON.stringify({ ...utm, referrer, ts: Date.now() }))
    }
    // 末次触点（每次访问更新）
    setStorage(LAST_TOUCH_KEY, JSON.stringify({ ...utm, referrer, ts: Date.now() }))

    // 会话：30 分钟无活动新建
    const now = Date.now()
    const lastActive = parseInt(getStorage(SESSION_LAST_KEY) || '0', 10)
    let sessionId = getStorage(SESSION_KEY, true)
    if (!sessionId || now - lastActive > SESSION_TIMEOUT) {
      sessionId = uuid()
      setStorage(SESSION_KEY, sessionId, true)
    }
    setStorage(SESSION_LAST_KEY, String(now), true)
    this.sessionId = sessionId

    // 启动批量上报定时器
    this.timer = setInterval(() => { try { this.flush() } catch (e) {} }, BATCH_INTERVAL)
    // 页面卸载前尽力上报
    window.addEventListener('beforeunload', () => { try { this.flush(true) } catch (e) {} })
  }

  // ---- 取当前上下文（首末次触点等）----
  _context(path, query) {
    let firstTouch = {}, lastTouch = {}
    try { firstTouch = JSON.parse(getStorage(FIRST_TOUCH_KEY) || '{}') } catch (e) {}
    try { lastTouch = JSON.parse(getStorage(LAST_TOUCH_KEY) || '{}') } catch (e) {}
    return {
      path: path || (typeof location !== 'undefined' ? location.pathname : ''),
      query: query || {},
      referrer: lastTouch.referrer || '',
      utm_source: lastTouch.utm_source, utm_medium: lastTouch.utm_medium,
      utm_campaign: lastTouch.utm_campaign, utm_term: lastTouch.utm_term, utm_content: lastTouch.utm_content,
      first_touch: firstTouch, last_touch: lastTouch
    }
  }

  // ---- 构造事件对象 ----
  _build(type, name, props = {}) {
    return {
      anonymousId: this.anonymousId,
      userId: this.userId,
      tenantId: this.tenantId,
      sessionId: this.sessionId,
      type, name,
      props,
      clientTs: Date.now()
    }
  }

  // ---- PV（try-catch 兜底，绝不让埋点报错中断路由）----
  pageView(route) {
    try {
      const ctx = this._context(route && route.path, route && route.query)
      this._enqueue(this._build('pageview', 'page_view', ctx))
    } catch (e) { /* 静默 */ }
  }

  // ---- 自定义事件 ----
  track(name, props = {}) {
    try {
      const ctx = this._context()
      this._enqueue(this._build('event', name, { ...ctx, ...props }))
    } catch (e) { /* 静默 */ }
  }

  // ---- 身份合并：登录时把匿名ID → userId/tenantId 发后端 ----
  identify(userId, traits = {}) {
    try {
      this.userId = String(userId)
      if (traits.tenantId) this.tenantId = traits.tenantId
      const evt = this._build('identify', 'identify', { userId: this.userId, tenantId: this.tenantId, ...traits })
      this._send(this.identifyEndpoint, [evt])
    } catch (e) { /* 静默：不影响登录流程 */ }
  }

  // ---- 入队 ----
  _enqueue(evt) {
    this.queue.push(evt)
    if (this.queue.length >= BATCH_SIZE) this.flush()
  }

  // ---- 批量上报 ----
  flush(sync = false) {
    if (!this.queue.length) return
    const batch = this.queue.splice(0, BATCH_SIZE)
    this._send(this.endpoint, batch, sync)
  }

  _send(url, body, sync = false) {
    if (typeof navigator === 'undefined') return
    const payload = JSON.stringify(body)
    // 优先 sendBeacon（fire-and-forget，不受 unload 影响）
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' })
      const ok = navigator.sendBeacon(url, blob)
      if (ok) return
    }
    // 降级 XHR（sync=true 时用同步 XHR 应对 beforeunload）
    try {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url, !sync)
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.send(payload)
    } catch (e) { /* 静默失败，埋点不影响业务 */ }
  }
}



// WEBPACK FOOTER //
// ./src/analytics/tracker.js